import { useId, useRef, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { TopicTooltipForFilter } from '@/features/connect-dots/components/tooltips/TopicTooltipForFilter';
import { Node } from '@/features/connect-dots/types/cytoscape';
import { TagIcon } from '@heroicons/react/24/outline';
import { TopicTag } from '@/features/analyze/components/Tag';
import { Core } from 'cytoscape'; // Cytoscape をインポート

export type TopicTagWithTooltipProps = {
  content: Node;
  cytoscapeCoreRefObject: Core | null; // Cytoscape インスタンスを追加
};

export const TopicTagWithFilterTooltip = ({
  content,
  cytoscapeCoreRefObject, // props で受け取る
}: TopicTagWithTooltipProps) => {
  const tooltipId = useId();
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isClickingRef = useRef(false); // クリック中かどうかを追跡

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;

    const update = () => {
      const r = triggerRef.current!.getBoundingClientRect();
      const left = r.left;
      const top = r.bottom + 4; // 4px margin
      setPos({ left, top });
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  const handleMouseEnter = () => {
    // タイマーをクリア
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setOpen(true);
  };

  const handleMouseLeave = () => {
    // クリック中の場合は閉じない
    if (isClickingRef.current) {
      return;
    }
    // 少し遅延を入れてTooltipに移動する時間を確保
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
    }, 100);
  };

  // ズーム関数: content.label に一致するノードにズーム
  const zoomToNodeByLabel = (cy: cytoscape.Core, label: string) => {
    const node = cy.nodes().filter((n) => n.data('label') === label);
    if (node.length === 1) {
      cy.animate({
        zoom: 0.8, // ズームレベル（適宜調整）
        center: { eles: node }, // ノードを中央に
      });
    } else {
      console.error(
        'Node with label not found or multiple nodes found:',
        label
      );
    }
  };

  const handleClick = () => {
    // クリック開始
    isClickingRef.current = true;

    // ズーム機能を実装
    if (cytoscapeCoreRefObject) {
      zoomToNodeByLabel(cytoscapeCoreRefObject, content.label);
    } else {
      console.error('Cytoscape instance not available');
    }

    // クリック終了（少し遅延を入れてツールチップが閉じないようにする）
    setTimeout(() => {
      isClickingRef.current = false;
    }, 300);
  };

  return (
    <>
      <div
        ref={triggerRef}
        className="relative inline-flex"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* タグ本体 */}
        <TopicTag
          icon={TagIcon}
          text={content.label}
          isSocialIssue={content.isSocialIssue ?? false}
          onClick={handleClick}
          aria-describedby={tooltipId}
          tabIndex={0}
        />
      </div>

      {open &&
        pos &&
        createPortal(
          <div
            id={tooltipId}
            className="z-50"
            style={{
              position: 'fixed',
              left: pos.left,
              top: pos.top,
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <TopicTooltipForFilter
              content={content}
              cytoscapeCoreRefObject={cytoscapeCoreRefObject}
              onClose={handleMouseLeave}
            />
          </div>,
          document.body
        )}
    </>
  );
};
